import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import IGroupSubsectionUI, { IGroupItemLeafUI } from "@models/IGroupSubsectionUI";
import ICatalog_Item from "@models/ICatalog_Item";
import IOnTypeaheadCatalogItemDTO from "@models/DTO/IOnTypeaheadCatalogItemDTO";
import ITypeaheadCatalogItemDTO from "@models/DTO/ITypeaheadCatalogItemDTO";
import IUiSocketSetup from "@models/IUiSocketSetup";
import EndpointsEnum from "@models/EndpointsEnum";
import { OwAccordion } from "@components/shared/OwAccordion/OwAccordion";
import Typeahead from "@components/shared/Typeahead/Typeahead";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import UserContext from "@contexts/UserContext";
import SupplierContext from "@contexts/SupplierContext";
import { SocketContext } from "@contexts/SocketContext";
import { useSortable, arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
    DragOverlay,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { v4 } from "uuid";
import { MultiProductGroupLeaf } from "./MultiProductGroupLeaf/MultiProductGroupLeaf";

type Props = {
    section: IGroupSubsectionUI;
    catalogGuid: string;
    updateSectionFn: (section: IGroupSubsectionUI) => void;
    id: string;
    openOverride?: boolean;
    isActive?: boolean;
    isDragOverlay?: boolean;
};

export const MultiProductGroupSection = (props: Props) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: props.id,
        data: { type: "multi-group-section" },
    });

    const style = props.isDragOverlay
        ? { position: "relative" as const, zIndex: 1000, opacity: 0.9 }
        : {
              transform: CSS.Transform.toString(transform),
              transition,
              opacity: isDragging ? 0.6 : 1,
              position: "relative" as const,
              zIndex: isDragging ? 1 : ("auto" as const),
          };

    const activeStyles = props.isActive
        ? { background: "#f7f9fc", boxShadow: "0 0 10px rgba(0,0,0,0.15)", border: "1px dashed #007bff" }
        : {};

    const overlayStyles = props.isDragOverlay
        ? { background: "#f7f9fc", boxShadow: "0 5px 16px rgba(0,0,0,0.2)", opacity: 0.9, border: "1px solid #007bff", width: "100%" }
        : {};

    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;

    const [editMode, setEditMode] = useState(!props.section.id);
    const [titleDraft, setTitleDraft] = useState(props.section.title);
    const [titleError, setTitleError] = useState<string | null>(null);

    const [foundItems, setFoundItems] = useState<ICatalog_Item[]>([]);
    const [selectedItem, setSelectedItem] = useState<ICatalog_Item | undefined>();
    const [clearTypeahead, setClearTypeahead] = useState(false);

    const [activeLeafId, setActiveLeafId] = useState<string | null>(null);
    const [activeLeaf, setActiveLeaf] = useState<IGroupItemLeafUI | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const onTypeaheadResults = (data: IOnTypeaheadCatalogItemDTO) => {
        if (data.state === props.section.uuid) {
            setFoundItems(data.catalog_items);
        }
    };

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, fn: onTypeaheadResults }];

    useEffect(() => {
        for (const s of sockets) socket.on(s.endpoint, s.fn);
        return () => {
            for (const s of sockets) socket.off(s.endpoint, s.fn);
        };
    }, []);

    // Commit title edit
    const commitTitle = () => {
        const trimmed = titleDraft.trim();
        if (!trimmed) {
            setTitleError("title_required");
            return;
        }
        if (trimmed.length > 100) {
            setTitleError("title_max_100");
            return;
        }
        setTitleError(null);
        setEditMode(false);
        props.updateSectionFn({ ...props.section, title: trimmed });
    };

    const cancelTitleEdit = () => {
        setTitleDraft(props.section.title);
        setTitleError(null);
        setEditMode(false);
    };

    const markForDelete = () => {
        props.updateSectionFn({ ...props.section, deleteFlag: !props.section.deleteFlag });
    };

    // Add leaf — prepend, seed group_title from item.name
    useEffect(() => {
        if (selectedItem) {
            const newLeaf: IGroupItemLeafUI = {
                uuid: v4(),
                catalog_item_id: selectedItem.id,
                Catalog_Item: selectedItem,
                group_sequence: 0,
                group_title: selectedItem.name,
            };
            props.updateSectionFn({ ...props.section, items: [newLeaf, ...props.section.items] });
            setSelectedItem(undefined);
            setClearTypeahead(true);
        }
    }, [selectedItem]);

    const findItems = (name: string) => {
        const data: ITypeaheadCatalogItemDTO = {
            name,
            catalogGuid: props.catalogGuid,
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            state: props.section.uuid,
        };
        socket.emit(EndpointsEnum.CatalogItemTypeaheadCatalogItem, data);
    };

    const clearTypeaheadFn = () => {
        setClearTypeahead(false);
        setSelectedItem(undefined);
    };

    // Update a leaf's fields (e.g. group_title edited inline)
    const updateLeafFn = (updatedLeaf: IGroupItemLeafUI) => {
        const items = props.section.items.map((l) => (l.uuid === updatedLeaf.uuid ? updatedLeaf : l));
        props.updateSectionFn({ ...props.section, items });
    };

    // Remove a leaf — id present → toggle deleteFlag; no id → splice
    const removeLeafFn = (leaf: IGroupItemLeafUI) => {
        let items: IGroupItemLeafUI[];
        if (leaf.id) {
            items = props.section.items.map((l) => (l.uuid === leaf.uuid ? { ...l, deleteFlag: !l.deleteFlag } : l));
        } else {
            items = props.section.items.filter((l) => l.uuid !== leaf.uuid);
        }
        props.updateSectionFn({ ...props.section, items });
    };

    const handleLeafDragStart = (event: DragStartEvent) => {
        const { active } = event;
        setActiveLeafId(active.id as string);
        const leaf = props.section.items.find((l) => l.uuid === active.id);
        if (leaf) setActiveLeaf(leaf);
    };

    const handleLeafDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const oldIdx = props.section.items.findIndex((l) => l.uuid === active.id);
            const newIdx = props.section.items.findIndex((l) => l.uuid === over.id);
            const reordered = arrayMove(props.section.items, oldIdx, newIdx).map((l, i) => ({
                ...l,
                group_sequence: i,
            }));
            props.updateSectionFn({ ...props.section, items: reordered });
        }
        setActiveLeafId(null);
        setActiveLeaf(null);
    };

    const leftElem = () => {
        if (editMode) {
            return (
                <div className={"d-flex align-items-center"}>
                    <i className={"fas fa-check text-success pointer me-2"} onClick={commitTitle} />
                    <i className={"fas fa-times text-secondary pointer me-2"} onClick={cancelTitleEdit} />
                    <input
                        type="text"
                        className={`form-control form-control-sm ${titleError ? "is-invalid" : ""}`}
                        style={{ maxWidth: "240px" }}
                        value={titleDraft}
                        maxLength={100}
                        autoFocus
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            setTitleDraft(e.target.value);
                            if (titleError) setTitleError(null);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") commitTitle();
                            if (e.key === "Escape") cancelTitleEdit();
                        }}
                    />
                    {titleError && (
                        <span className={"text-danger small ms-2"}>
                            <OwLabel title={titleError} noBottomMargin />
                        </span>
                    )}
                </div>
            );
        }

        return (
            <div className={"d-flex align-items-center"}>
                <div className="drag-handle me-2" {...listeners}>
                    <i className="fas fa-grip-lines" />
                </div>
                <i className={"fas fa-pencil-alt text-primary pointer me-2"} onClick={() => setEditMode(true)} />
                <i
                    className={"fas pointer me-2 " + (props.section.deleteFlag ? "fa-undo text-secondary" : "fa-trash-alt text-danger")}
                    onClick={markForDelete}
                />
                <span className={props.section.deleteFlag ? "text-decoration-line-through text-danger" : ""}>{props.section.title}</span>
            </div>
        );
    };

    const visibleLeaves = props.section.items.filter((l) => !l.deleteFlag);

    return (
        <div
            className={"MultiProductGroupSection"}
            ref={setNodeRef}
            style={{ ...style, ...activeStyles, ...overlayStyles }}
            {...attributes}>
            <OwAccordion leftElem={leftElem()} initOpen={!props.section.id} chevronSide={"RIGHT"} openOverride={props.openOverride}>
                <div
                    className={"ms-4"}
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}>
                    {props.section.deleteFlag ? (
                        <div className={"text-muted small fst-italic py-2"}>
                            <OwLabel title={"subsection_will_be_deleted_on_save"} noBottomMargin />
                        </div>
                    ) : (
                        <>
                            <div className={"mb-2"}>
                                <OwLabel title={"items"} />
                                <Typeahead
                                    isSelectableFn={() => true}
                                    typeaheadApiFn={findItems}
                                    confirmAdd={true}
                                    typeaheadItems={foundItems}
                                    titleProp={"name"}
                                    subTitleProp={"sku"}
                                    selectedItemFn={setSelectedItem}
                                    clearTypeahead={clearTypeahead}
                                    clearTypeaheadCallback={clearTypeaheadFn}
                                    selectedItem={selectedItem}
                                    selectedItemTitleProp={"name"}
                                    placeholder={"search"}
                                />
                            </div>

                            {visibleLeaves.length === 0 && (
                                <div className={"text-muted small fst-italic mb-2"}>
                                    <OwLabel title={"no_items_added"} noBottomMargin />
                                </div>
                            )}

                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragStart={handleLeafDragStart}
                                onDragEnd={handleLeafDragEnd}>
                                <SortableContext items={props.section.items.map((l) => l.uuid)} strategy={verticalListSortingStrategy}>
                                    {props.section.items.map((leaf) => (
                                        <MultiProductGroupLeaf
                                            key={leaf.uuid}
                                            leaf={leaf}
                                            id={leaf.uuid}
                                            updateLeafFn={updateLeafFn}
                                            removeLeafFn={removeLeafFn}
                                            isActive={activeLeafId === leaf.uuid}
                                        />
                                    ))}
                                </SortableContext>
                                <DragOverlay adjustScale={true}>
                                    {activeLeaf && (
                                        <MultiProductGroupLeaf
                                            leaf={activeLeaf}
                                            id={activeLeaf.uuid}
                                            updateLeafFn={() => {}}
                                            removeLeafFn={() => {}}
                                            isDragOverlay={true}
                                        />
                                    )}
                                </DragOverlay>
                            </DndContext>
                        </>
                    )}
                </div>
            </OwAccordion>
        </div>
    );
};
